import { ArrowLeft, ArrowRight, Info, Clock, Target, Timer, CheckCircle, Circle } from 'lucide-react';
import type { 
  MagicPrioritizationItem, 
  ScoringPhase, 
  UrgencyLevel, 
  ValueLevel, 
  DurationLevel 
} from '../types';
import { URGENCY_LABELS, VALUE_LABELS, DURATION_LABELS } from '../types';

interface ScoringViewProps {
  item: MagicPrioritizationItem;
  items: MagicPrioritizationItem[]; // All items for showing progress
  itemIndex: number;
  totalItems: number;
  phase: ScoringPhase;
  onUrgencyUpdate: (value: UrgencyLevel) => void;
  onValueUpdate: (value: ValueLevel) => void;
  onDurationUpdate: (value: DurationLevel) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
  canGoBack: boolean;
  progress: number;
}

export function ScoringView({
  item,
  items,
  itemIndex,
  totalItems,
  phase,
  onUrgencyUpdate,
  onValueUpdate,
  onDurationUpdate,
  onNext,
  onBack,
  canProceed,
  canGoBack,
  progress,
}: ScoringViewProps) {
  const phaseConfig = {
    urgency: {
      icon: Clock,
      title: 'Urgency',
      subtitle: 'How time-sensitive is this item?',
      description: 'Focus ONLY on time sensitivity. Ask: "What specifically gets worse if we wait 3-6 months?" Ignore value and effort for now.',
      tip: 'Distinguish between real urgency (delay causes escalating harm or lost opportunity) and false urgency (it\'s been on the list forever). An outage is urgent. "We should do this" for years is not.',
      color: 'red',
      phaseNumber: 1,
    },
    value: {
      icon: Target,
      title: 'Value',
      subtitle: 'What is the impact magnitude?',
      description: 'Focus ONLY on value/impact. Urgency is already locked. Ask: "What outcome changes if we do this, and how much?"',
      tip: 'A "small win" isn\'t very valuable—but it could still be worth doing. Don\'t conflate value with size or effort. A Game Changer transforms outcomes; a Tweak makes incremental improvements.',
      color: 'green',
      phaseNumber: 2,
    },
    duration: {
      icon: Timer,
      title: 'Duration',
      subtitle: 'Time to first meaningful payoff?',
      description: 'Urgency and value are locked. Estimate: "How long until we get the first meaningful payoff or decision-grade evidence?"',
      tip: 'Duration is time-to-realize-value, not just time-to-build. A quick win with same urgency and value should be done first. Shorter durations score higher in the final ranking.',
      color: 'blue',
      phaseNumber: 3,
    },
  };

  const config = phaseConfig[phase];
  const Icon = config.icon;

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string; ring: string; selectedBg: string; selectedBorder: string }> = {
      red: {
        bg: 'bg-[var(--coral-dim)]',
        border: 'border-[var(--coral)]',
        text: 'text-[var(--coral)]',
        ring: 'ring-red-500',
        selectedBg: 'bg-[var(--coral-dim)]',
        selectedBorder: 'border-[var(--coral)]',
      },
      green: {
        bg: 'bg-[var(--green-dim)]',
        border: 'border-[var(--green)]',
        text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
        ring: 'ring-green-500',
        selectedBg: 'bg-[var(--green-dim)]',
        selectedBorder: 'border-[var(--green)]',
      },
      blue: {
        bg: 'bg-[var(--slate-dim)]',
        border: 'border-[var(--slate)]',
        text: 'text-[var(--text-sec)]',
        ring: 'ring-blue-500',
        selectedBg: 'bg-[var(--slate-dim)]',
        selectedBorder: 'border-[var(--slate)]',
      },
    };
    return colors[color] || colors.blue;
  };

  const colors = getColorClasses(config.color);

  // Check if an item has been scored for the current phase
  const isItemScoredForPhase = (item: MagicPrioritizationItem): boolean => {
    switch (phase) {
      case 'urgency': return item.urgency !== null;
      case 'value': return item.value !== null;
      case 'duration': return item.duration !== null;
    }
  };

  // Get the score label for an item in the current phase
  const getItemScoreLabel = (item: MagicPrioritizationItem): string | null => {
    switch (phase) {
      case 'urgency': return item.urgency ? URGENCY_LABELS[item.urgency].label : null;
      case 'value': return item.value ? VALUE_LABELS[item.value].label : null;
      case 'duration': return item.duration ? DURATION_LABELS[item.duration].label : null;
    }
  };

  const renderUrgencyOptions = () => {
    const options: { value: Exclude<UrgencyLevel, null>; colorClass: string }[] = [
      { value: 'asap', colorClass: 'border-[var(--coral)] bg-[var(--coral-dim)] hover:bg-[var(--coral-dim)]' },
      { value: 'soon', colorClass: 'border-[var(--mustard)] bg-[var(--mustard-dim)] hover:bg-[var(--mustard-dim)]' },
      { value: 'whenever', colorClass: 'border-[var(--rule)] bg-[var(--surface)] hover:bg-[var(--hover-tint)]' },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {options.map((option) => {
          const label = URGENCY_LABELS[option.value];
          const isSelected = item.urgency === option.value;
          return (
            <button
              key={option.value}
              onClick={() => onUrgencyUpdate(option.value)}
              className={`p-4  border-2 text-left transition-all ${
                isSelected
                  ? `${option.colorClass} ring-2 ring-offset-2 ring-purple-500`
                  : `border-[var(--rule)] bg-[var(--bg-raised)] hover:${option.colorClass}`
              }`}
            >
              <div className="font-bold text-lg text-[var(--text-pri)] mb-1">{label.label}</div>
              <div className="text-sm text-[var(--text-sec)]">{label.description}</div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderValueOptions = () => {
    const options: { value: Exclude<ValueLevel, null>; colorClass: string }[] = [
      { value: 'game-changer', colorClass: 'border-[var(--green)] bg-[var(--green-dim)] hover:bg-[var(--green-dim)]' },
      { value: 'optimizer', colorClass: 'border-[var(--slate)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)]' },
      { value: 'tweak', colorClass: 'border-[var(--rule)] bg-[var(--surface)] hover:bg-[var(--hover-tint)]' },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {options.map((option) => {
          const label = VALUE_LABELS[option.value];
          const isSelected = item.value === option.value;
          return (
            <button
              key={option.value}
              onClick={() => onValueUpdate(option.value)}
              className={`p-4  border-2 text-left transition-all ${
                isSelected
                  ? `${option.colorClass} ring-2 ring-offset-2 ring-purple-500`
                  : `border-[var(--rule)] bg-[var(--bg-raised)] hover:${option.colorClass}`
              }`}
            >
              <div className="font-bold text-lg text-[var(--text-pri)] mb-1">{label.label}</div>
              <div className="text-sm text-[var(--text-sec)]">{label.description}</div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderDurationOptions = () => {
    const options: { value: Exclude<DurationLevel, null>; colorClass: string }[] = [
      { value: '1-3-months', colorClass: 'border-[var(--green)] bg-[var(--green-dim)] hover:bg-[var(--green-dim)]' },
      { value: '1-3-quarters', colorClass: 'border-[var(--mustard)] bg-[var(--mustard-dim)] hover:bg-[var(--mustard-dim)]' },
      { value: '1-3-years', colorClass: 'border-[var(--mustard)] bg-[var(--mustard-dim)] hover:bg-[var(--mustard-dim)]' },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {options.map((option) => {
          const label = DURATION_LABELS[option.value];
          const isSelected = item.duration === option.value;
          return (
            <button
              key={option.value}
              onClick={() => onDurationUpdate(option.value)}
              className={`p-4  border-2 text-left transition-all ${
                isSelected
                  ? `${option.colorClass} ring-2 ring-offset-2 ring-purple-500`
                  : `border-[var(--rule)] bg-[var(--bg-raised)] hover:${option.colorClass}`
              }`}
            >
              <div className="font-bold text-lg text-[var(--text-pri)] mb-1">{label.label}</div>
              <div className="text-sm text-[var(--text-sec)]">{label.description}</div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderOptions = () => {
    switch (phase) {
      case 'urgency':
        return renderUrgencyOptions();
      case 'value':
        return renderValueOptions();
      case 'duration':
        return renderDurationOptions();
    }
  };

  // Show previously set values for THIS item as context (from prior phases)
  const renderPreviousSelections = () => {
    const selections = [];
    
    if (phase !== 'urgency' && item.urgency) {
      selections.push({
        label: 'Urgency',
        value: URGENCY_LABELS[item.urgency].label,
        locked: true,
      });
    }
    
    if (phase === 'duration' && item.value) {
      selections.push({
        label: 'Value',
        value: VALUE_LABELS[item.value].label,
        locked: true,
      });
    }

    if (selections.length === 0) return null;

    return (
      <div className="mb-4 flex flex-wrap gap-2">
        <span className="text-xs text-[var(--text-ter)] mr-1">This item's prior scores:</span>
        {selections.map((sel, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full text-sm"
          >
            <span className="font-medium">{sel.label}:</span>
            <span>{sel.value}</span>
            {sel.locked && <span className="text-[var(--text-ter)] text-xs">(locked)</span>}
          </span>
        ))}
      </div>
    );
  };

  // Get the next button text based on where we are in the flow
  const getNextButtonText = (): string => {
    const isLastItemInPhase = itemIndex === totalItems - 1;
    const isLastPhase = phase === 'duration';
    
    if (isLastItemInPhase && isLastPhase) {
      return 'See Results';
    }
    if (isLastItemInPhase) {
      const nextPhase = phase === 'urgency' ? 'Value' : 'Duration';
      return `Finish ${config.title} → Start ${nextPhase}`;
    }
    return 'Next Item';
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Phase Header - Shows which dimension we're scoring */}
      <div className={`mb-6 p-4 ${colors.bg}  border ${colors.border}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${colors.selectedBg} `}>
              <Icon className={`h-6 w-6 ${colors.text}`} />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${colors.text}`}>
                Phase {config.phaseNumber} of 3: {config.title}
              </h2>
              <p className="text-sm text-[var(--text-sec)]">
                Rating ALL items on {config.title.toLowerCase()} before moving to next dimension
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-[var(--text-pri)]">
              {itemIndex + 1} / {totalItems}
            </div>
            <div className="text-xs text-[var(--text-ter)]">items in this phase</div>
          </div>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-[var(--text-sec)]">
            Overall Progress
          </span>
          <span className="text-sm font-medium text-[var(--text-sec)]">
            {Math.round(progress)}% complete
          </span>
        </div>
        <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
          <div
            className="bg-[var(--slate)] h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        {/* Phase indicators */}
        <div className="flex mt-2 gap-1">
          <div className={`flex-1 h-1 rounded ${phase === 'urgency' || phase === 'value' || phase === 'duration' ? 'bg-[var(--coral)]' : 'bg-[var(--rule-bright)]'}`} />
          <div className={`flex-1 h-1 rounded ${phase === 'value' || phase === 'duration' ? 'bg-[var(--green)]' : 'bg-[var(--rule-bright)]'}`} />
          <div className={`flex-1 h-1 rounded ${phase === 'duration' ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'}`} />
        </div>
        <div className="flex mt-1 text-xs text-[var(--text-ter)]">
          <span className="flex-1 text-center">Urgency</span>
          <span className="flex-1 text-center">Value</span>
          <span className="flex-1 text-center">Duration</span>
        </div>
      </div>

      {/* Items Progress in Current Phase */}
      <div className="mb-6 bg-[var(--surface)] p-4 border border-[var(--rule)]">
        <h3 className="text-sm font-medium text-[var(--text-sec)] mb-3">
          Items in {config.title} Phase:
        </h3>
        <div className="flex flex-wrap gap-2">
          {items.map((it, idx) => {
            const isScored = isItemScoredForPhase(it);
            const isCurrent = idx === itemIndex;
            const scoreLabel = getItemScoreLabel(it);
            
            return (
              <div
                key={it.id}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                  isCurrent 
                    ? `${colors.bg} ${colors.border} border-2 font-medium`
                    : isScored
                      ? 'bg-[var(--bg-raised)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
                      : 'bg-[var(--bg-raised)] border border-[var(--rule)] text-[var(--text-ter)]'
                }`}
              >
                {isScored ? (
                  <CheckCircle className="h-3.5 w-3.5 text-[var(--green)]" />
                ) : isCurrent ? (
                  <Circle className={`h-3.5 w-3.5 ${colors.text}`} />
                ) : (
                  <Circle className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                )}
                <span className="truncate max-w-[120px]" title={it.name}>
                  {it.name}
                </span>
                {isScored && scoreLabel && !isCurrent && (
                  <span className="text-xs text-[var(--text-ter)]">({scoreLabel})</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Scoring Card */}
      <div className="bg-[var(--bg-raised)] shadow-md border border-[var(--rule)] p-8 mb-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
            {item.name}
          </h2>
          {renderPreviousSelections()}
        </div>

        <div className="mb-6">
          <label className="block text-lg font-semibold text-[var(--text-pri)] mb-2">
            {config.subtitle}
          </label>
          <p className="text-sm text-[var(--text-sec)] mb-4">
            {config.description}
          </p>
          
          {renderOptions()}
        </div>

        {/* Tip Box */}
        <div className={`p-4 ${colors.bg}  border ${colors.border} mb-6`}>
          <div className="flex items-start gap-2">
            <Info className={`h-5 w-5 ${colors.text} flex-shrink-0 mt-0.5`} />
            <div className={`text-sm ${colors.text}`}>
              <strong>The Trick:</strong> {config.tip}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={onBack}
            disabled={!canGoBack}
            className="flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)] disabled:cursor-not-allowed transition-colors font-medium"
          >
            {getNextButtonText()}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
